/**
 * Playbooks-Reiter auf /wissen (S1.4, KONZEPT-GEDAECHTNIS.md § 2b). Editor fuer Weg (a) der drei
 * Entstehungswege: Liste, Anlegen/Bearbeiten (Markdown-Text, hart gelintet beim Speichern),
 * Aktivieren (nur Owner/Admin) und Versions-Historie je Playbook.
 */
import { useState, type FormEvent } from "react";
import { PLAYBOOK_VORLAGEN } from "@maestro/playbook-vorlagen";
import { useApi, apiMutate } from "../hooks/useApi";
import { currentMembership } from "../lib/membership";
import type { Me } from "../ui/types";
import { PILL_CLASSES, formatDateTime } from "../ui/format";
import { Button, Card, Drawer, EmptyState, ErrorNote, Icon, Input, LoadingRows, Modal, Textarea } from "../ui/components";
import { useContentRefetch } from "../ui/CopilotProvider";

interface Playbook {
  id: string;
  name: string;
  bodyMd: string;
  status: "draft" | "active" | "archived";
  origin: "bediener" | "agent" | "betreiber_vorlage";
  prioritaet: number;
  version: number;
  updatedAt: string;
  /** S4.2: bei übernommenen Vorlagen steht hier `[{ vorlage: "<schluessel>" }]`. */
  evidenceRefs?: Array<{ vorlage?: string }> | null;
}

interface PlaybookVersion {
  id: string;
  version: number;
  bodyMd: string;
  status: string;
  geaendertVon: string | null;
  createdAt: string;
}

const EMPTY = { name: "", bodyMd: "", prioritaet: 100 };
type Form = typeof EMPTY;

const STATUS_LABEL: Record<Playbook["status"], string> = { draft: "Entwurf", active: "Aktiv", archived: "Archiviert" };
// Design E9: Aktiv gruen, Entwurf neutral, Archiviert neutral (gedimmt) — wie ueberall.
const STATUS_BADGE: Record<Playbook["status"], string> = {
  draft: PILL_CLASSES.neutral,
  active: PILL_CLASSES.success,
  archived: `${PILL_CLASSES.neutral} opacity-70`,
};
const badge = "rounded-full px-2 py-0.5 font-label-md text-label-md";
// S3.2 "Lint-Warnampel" - WEICHE Einschaetzung (Widerspruch zu anderem Playbook/Fakt), getrennt
// von den harten, farblich anders codierten STATUS_BADGE-Zustaenden oben.
const WARNAMPEL_LABEL: Record<"gruen" | "gelb" | "rot", string> = { gruen: "Kein Widerspruch erkennbar", gelb: "Möglicher Widerspruch", rot: "Widerspruch erkannt" };
const WARNAMPEL_BADGE: Record<"gruen" | "gelb" | "rot", string> = {
  gruen: PILL_CLASSES.success,
  gelb: PILL_CLASSES.warn,
  rot: PILL_CLASSES.warn, // Ruhige Hinweise: ein Widerspruch ist ein Hinweis, kein Fehler.
};

/**
 * UI-Fehleruebersetzung. Die Routen liefern zu jedem Fehler bereits einen deutschen
 * `message`-Text (toApiError liest ihn), dieser Fallback greift, wenn nur der nackte Code
 * ankommt - dann stuende dem Bediener sonst "forbidden_role" im Kasten (Bughunt § 12.2, gleiche
 * Beobachtung wie beim Katalog, s. catalog.tsx).
 */
function fehlertext(err: unknown): string {
  const roh = err instanceof Error ? err.message : String(err);
  if (/^forbidden_role$/i.test(roh.trim())) {
    return "Nur Inhaber dürfen ein aktives Playbook aktivieren oder inhaltlich ändern. Entwürfe darf jedes Mitglied bearbeiten.";
  }
  if (/^lint_blocked$/i.test(roh.trim())) return "Der Text verletzt eine harte Playbook-Regel und wurde nicht gespeichert.";
  if (/^not_a_draft$/i.test(roh.trim())) return "Nur Entwürfe lassen sich löschen - ein aktives Playbook bitte archivieren.";
  return roh;
}

export function PlaybooksTab() {
  const { data, loading, error, reload } = useApi<Playbook[]>("/api/playbooks");
  const { data: me } = useApi<Me>("/api/me");
  const membership = currentMembership(me);
  const istInhaber = membership?.role === "owner";

  // S3.1: propose_playbook meldet changed:{resource:"playbook"} (maestro-agent.ts) - ohne dieses
  // Abonnement bliebe eine offene /wissen-Seite nach einem im Chat freigegebenen Entwurf veraltet
  // (dasselbe Muster wie knowledge.tsx fuer knowledge_write).
  useContentRefetch("playbook", () => {
    reload();
  });

  const playbooks = data ?? [];

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  /** Bestaetigung vor dem endgueltigen Loeschen eines Entwurfs (destruktiv, wie im Angebots-
   * Builder: kein Sofort-Loeschen ohne Rueckfrage). */
  const [loeschenId, setLoeschenId] = useState<string | null>(null);
  // S3.2 ("Lint-Warnampel"): on-demand, nicht persistiert - rein lokaler UI-State, ein neuer
  // Aufruf ersetzt das letzte Ergebnis. Keyed nach Playbook-Id, damit mehrere Zeilen unabhaengig
  // pruefbar sind.
  const [warnampel, setWarnampel] = useState<Record<string, { ampel: "gruen" | "gelb" | "rot"; begruendung: string } | "loading" | "error">>({});

  async function warnampelPruefen(id: string) {
    setWarnampel((w) => ({ ...w, [id]: "loading" }));
    try {
      const res = await apiMutate<{ data: { ampel: "gruen" | "gelb" | "rot"; begruendung: string } }>(`/api/playbooks/${id}/warnampel`, "POST");
      setWarnampel((w) => ({ ...w, [id]: res.data }));
    } catch {
      setWarnampel((w) => ({ ...w, [id]: "error" }));
    }
  }

  function openNew() {
    setEditId(null);
    setForm(EMPTY);
    setFormError(null);
    setOpen(true);
  }
  function openEdit(p: Playbook) {
    setEditId(p.id);
    setForm({ name: p.name, bodyMd: p.bodyMd, prioritaet: p.prioritaet });
    setFormError(null);
    setOpen(true);
  }

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      const body = { name: form.name, bodyMd: form.bodyMd, prioritaet: form.prioritaet };
      if (editId) await apiMutate(`/api/playbooks/${editId}`, "PATCH", body);
      else await apiMutate("/api/playbooks", "POST", body);
      setOpen(false);
      reload();
    } catch (err) {
      setFormError(fehlertext(err));
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(id: string, status: "active" | "archived" | "draft") {
    setBusy(true);
    setFormError(null);
    try {
      await apiMutate(`/api/playbooks/${id}`, "PATCH", { status });
      reload();
    } catch (err) {
      setFormError(fehlertext(err));
    } finally {
      setBusy(false);
    }
  }

  /** Nur fuer Entwuerfe (02.08.2026, Antoine: "Warum kann ich Entwuerfe nicht loeschen?"). Ein
   * aktives/archiviertes Playbook bleibt erhalten - dafuer ist "Archivieren" der Weg, damit die
   * Versions-Historie nicht verschwindet. Der Server zieht dieselbe Grenze (409 not_a_draft). */
  async function entwurfLoeschen(id: string) {
    setBusy(true);
    setFormError(null);
    try {
      await apiMutate(`/api/playbooks/${id}`, "DELETE");
      setLoeschenId(null);
      reload();
    } catch (err) {
      setFormError(fehlertext(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {formError && <ErrorNote message={formError} />}

      <VorlagenKatalog playbooks={playbooks} onUebernommen={reload} onFehler={setFormError} />

      <div className="mb-3 flex justify-end">
        <Button type="button" onClick={openNew}>
          <Icon name="add" className="text-[18px]" /> Skill hinzufügen
        </Button>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <LoadingRows rows={3} />
        ) : error ? (
          <ErrorNote message={error} />
        ) : playbooks.length === 0 ? (
          <EmptyState
            icon="auto_stories"
            title="Noch keine Skills"
            hint='Legen Sie oben rechts einen Skill an — eine kurze Anweisung, wie der Assistent in einer bestimmten Lage handeln soll (z. B. „Bei veganen Anfragen immer auf das Buffet hinweisen").'
          />
        ) : (
          <ul className="divide-y divide-outline-variant/20 dark:divide-outline/20">
            {playbooks.map((p) => (
              <li key={p.id} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{p.name}</p>
                      <span className={`${badge} ${STATUS_BADGE[p.status]}`}>{STATUS_LABEL[p.status]}</span>
                      {p.origin === "agent" && <span className={`${badge} bg-primary-container text-on-primary-container`}>im Chat notiert</span>}
                    </div>
                    <p className="mt-1 line-clamp-2 font-body-sm text-body-sm text-on-surface-variant">{p.bodyMd}</p>
                    <p className="mt-1 font-label-md text-label-md text-secondary dark:text-secondary-fixed-dim">
                      Version {p.version} · zuletzt geändert {formatDateTime(p.updatedAt)}
                    </p>
                    {warnampel[p.id] && warnampel[p.id] !== "loading" && (
                      <div className="mt-2">
                        {warnampel[p.id] === "error" ? (
                          <ErrorNote message="Widerspruchsprüfung fehlgeschlagen. Bitte erneut versuchen." />
                        ) : (
                          <div className="flex items-start gap-2">
                            <span className={`${badge} shrink-0 ${WARNAMPEL_BADGE[(warnampel[p.id] as { ampel: "gruen" | "gelb" | "rot" }).ampel]}`}>
                              {WARNAMPEL_LABEL[(warnampel[p.id] as { ampel: "gruen" | "gelb" | "rot" }).ampel]}
                            </span>
                            <p className="font-body-sm text-body-sm text-on-surface-variant">{(warnampel[p.id] as { begruendung: string }).begruendung}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <div className="flex items-center gap-3">
                      {/* Bughunt 24.08.2026 § 12.2: ein AKTIVES Playbook wirkt im System-Prompt
                          des Assistenten - seinen Inhalt aendern duerfen nur Inhaber
                          (Gate in routes/playbooks.ts am PATCH). Entwuerfe und Archiviertes
                          bleiben fuer jedes Mitglied editierbar. Gleiche Darstellung wie beim
                          "Aktivieren"-Knopf: sichtbar, aber gesperrt mit Begruendung im title -
                          ein verschwundener Knopf erklaert nichts. */}
                      <button
                        type="button"
                        disabled={p.status === "active" && !istInhaber}
                        title={p.status === "active" && !istInhaber ? "Nur Inhaber dürfen ein aktives Playbook inhaltlich ändern" : undefined}
                        className="inline-flex items-center gap-1 font-label-md text-label-md text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-40 disabled:no-underline"
                        onClick={() => openEdit(p)}
                      >
                        <Icon name="edit" className="text-[16px]" /> Bearbeiten
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 font-label-md text-label-md text-on-surface-variant hover:underline"
                        onClick={() => setHistoryId(historyId === p.id ? null : p.id)}
                      >
                        <Icon name="history" className="text-[16px]" /> Historie
                      </button>
                      <button
                        type="button"
                        disabled={warnampel[p.id] === "loading"}
                        className="inline-flex items-center gap-1 font-label-md text-label-md text-on-surface-variant hover:underline disabled:opacity-50"
                        onClick={() => void warnampelPruefen(p.id)}
                        title="Prüft weiche Konflikte mit anderen aktiven Skills/bekannten Fakten - ersetzt nicht die harte Regelprüfung beim Speichern."
                      >
                        <Icon name="rule" className="text-[16px]" /> {warnampel[p.id] === "loading" ? "Prüft…" : "Auf Widersprüche prüfen"}
                      </button>
                    </div>
                    {p.status === "draft" && (
                      <button
                        type="button"
                        title={istInhaber ? undefined : "Nur Inhaber dürfen aktivieren"}
                        disabled={busy || !istInhaber}
                        className="inline-flex items-center gap-1 font-label-md text-label-md text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-40 disabled:no-underline"
                        onClick={() => void setStatus(p.id, "active")}
                      >
                        <Icon name="check_circle" className="text-[16px]" /> Aktivieren
                      </button>
                    )}
                    {p.status !== "archived" && (
                      <button
                        type="button"
                        disabled={busy}
                        className="inline-flex items-center gap-1 font-label-md text-label-md text-on-surface-variant hover:underline disabled:opacity-50 dark:text-secondary-fixed-dim"
                        onClick={() => void setStatus(p.id, "archived")}
                      >
                        <Icon name="archive" className="text-[16px]" /> Archivieren
                      </button>
                    )}
                    {/* Endgueltig loeschen NUR fuer Entwuerfe: ein nie aktiviertes Playbook hat
                        keine Historie, die es zu bewahren gaebe. Aktive/archivierte bleiben
                        erhalten - dort ist "Archivieren" der richtige Weg. */}
                    {p.status === "draft" && (
                      <button
                        type="button"
                        disabled={busy}
                        title="Entwurf endgültig löschen"
                        className="inline-flex items-center gap-1 font-label-md text-label-md text-error hover:underline disabled:opacity-50"
                        onClick={() => setLoeschenId(p.id)}
                      >
                        <Icon name="delete" className="text-[16px]" /> Löschen
                      </button>
                    )}
                  </div>
                </div>
                {historyId === p.id && <VersionHistory playbookId={p.id} />}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={loeschenId != null}
        onClose={() => setLoeschenId(null)}
        width="440px"
        gefahr
        title={<>Entwurf „{playbooks.find((p) => p.id === loeschenId)?.name}“ löschen?</>}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setLoeschenId(null)} disabled={busy}>
              Abbrechen
            </Button>
            <Button type="button" variant="danger" disabled={busy} onClick={() => loeschenId && void entwurfLoeschen(loeschenId)}>
              <Icon name="delete_forever" className="text-[16px]" /> Endgültig löschen
            </Button>
          </>
        }
      >
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          Der Entwurf wird endgültig entfernt. Das lässt sich nicht rückgängig machen.
        </p>
      </Modal>

      <Drawer open={open} onClose={() => setOpen(false)} title={editId ? "Skill bearbeiten" : "Skill hinzufügen"}>
        <form className="space-y-4" onSubmit={(e) => void save(e)}>
          {formError && <ErrorNote message={formError} />}
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Name *</span>
            <Input required placeholder="z. B. Vegane Anfragen" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Anweisung * <span className="font-normal text-on-surface-variant">({form.bodyMd.length}/2000)</span>
            </span>
            <Textarea
              required
              rows={8}
              maxLength={2000}
              placeholder="Was soll der Assistent in dieser Lage tun? Keine Preise, keine Freigabe-Umgehung, keine Kontaktdaten."
              value={form.bodyMd}
              onChange={(e) => setForm((f) => ({ ...f, bodyMd: e.target.value }))}
            />
            <span className="mt-1 block font-label-md text-label-md text-on-surface-variant">
              Wird beim Speichern automatisch geprüft: keine Preiszusagen, keine Freigabe-Umgehung, keine externen
              Weiterleitungen, keine Zugangsdaten, keine Kundendaten.
            </span>
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button type="submit" disabled={busy}>{busy ? "Speichert …" : "Speichern"}</Button>
          </div>
        </form>
      </Drawer>
    </>
  );
}

function VersionHistory({ playbookId }: { playbookId: string }) {
  const { data, loading } = useApi<PlaybookVersion[]>(`/api/playbooks/${playbookId}/versions`);
  const versions = data ?? [];
  return (
    <div className="mt-3 rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-3 dark:border-white/10 dark:bg-white/[0.03]">
      {loading ? (
        <LoadingRows rows={2} />
      ) : versions.length === 0 ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant">Noch keine Historie.</p>
      ) : (
        <ul className="space-y-2">
          {versions.map((v) => (
            <li key={v.id} className="flex items-start justify-between gap-3 font-body-sm text-body-sm">
              <div className="min-w-0">
                <span className="font-semibold text-on-background dark:text-inverse-on-surface">Version {v.version}</span>{" "}
                <span className={`${badge} ${STATUS_BADGE[v.status as Playbook["status"]] ?? ""}`}>{STATUS_LABEL[v.status as Playbook["status"]] ?? v.status}</span>
                <p className="mt-0.5 line-clamp-2 text-on-surface-variant">{v.bodyMd}</p>
              </div>
              <span className="shrink-0 font-label-md text-label-md text-secondary dark:text-secondary-fixed-dim">{formatDateTime(v.createdAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * S4.2 — der Betreiber-Vorlagenkatalog (KONZEPT-GEDAECHTNIS § 2b, „Ausbaustufe:
 * Betreiber-Vorlagen"). Der Katalog selbst ist Code (`@maestro/playbook-vorlagen`) und wird
 * deshalb NICHT über die API geladen: es gibt nichts nachzuschlagen, was nicht schon im Bundle
 * liegt. Nur das Übernehmen geht an den Server — dort entsteht die Mandantenzeile mit
 * `origin='betreiber_vorlage'`.
 *
 * Übernehmen erzeugt einen ENTWURF, nicht mehr. Das steht auch im sichtbaren Text: eine Vorlage,
 * die sich beim Anklicken selbst scharfschaltet, wäre eine Anweisung des Software-Betreibers, die
 * ungefragt im Betrieb eines Kunden wirkt.
 */
export function VorlagenKatalog({
  playbooks,
  onUebernommen,
  onFehler,
}: {
  playbooks: Playbook[];
  onUebernommen: () => void;
  onFehler: (m: string | null) => void;
}) {
  const [offen, setOffen] = useState(false);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  const uebernommen = new Set(
    playbooks.flatMap((p) => (p.evidenceRefs ?? []).map((r) => r?.vorlage).filter((s): s is string => typeof s === "string")),
  );
  const offeneVorlagen = PLAYBOOK_VORLAGEN.filter((v) => !uebernommen.has(v.schluessel));

  async function uebernehmen(schluessel: string) {
    setLaeuft(schluessel);
    onFehler(null);
    try {
      await apiMutate(`/api/playbooks/vorlagen/${encodeURIComponent(schluessel)}`, "POST", {});
      onUebernommen();
    } catch (err) {
      onFehler(err instanceof Error ? err.message : "Vorlage konnte nicht übernommen werden.");
    } finally {
      setLaeuft(null);
    }
  }

  if (offeneVorlagen.length === 0) return null;

  return (
    <Card className="mb-3 p-4">
      <button type="button" className="flex w-full items-center justify-between gap-3 text-left" onClick={() => setOffen(!offen)}>
        <span>
          <span className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Vorlagen von MAESTRO</span>
          <span className="ml-2 font-label-md text-label-md text-secondary dark:text-secondary-fixed-dim">{offeneVorlagen.length} verfügbar</span>
        </span>
        <Icon name={offen ? "expand_less" : "expand_more"} className="text-[20px] text-on-surface-variant" />
      </button>
      {offen && (
        <>
          <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant">
            Geprüfte Skills, die in fast jedem Betrieb passen. Übernehmen legt einen <strong>Entwurf</strong> an, den Sie frei
            ändern können — aktiv wird er erst, wenn Sie ihn aktivieren. Übernommene Vorlagen stehen bei einem Widerspruch
            über Ihren eigenen Skills.
          </p>
          <ul className="mt-3 divide-y divide-outline-variant/20 dark:divide-outline/20">
            {offeneVorlagen.map((v) => (
              <li key={v.schluessel} className="flex items-start justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{v.name}</p>
                  <p className="mt-0.5 font-body-sm text-body-sm text-on-surface-variant">{v.wofuer}</p>
                </div>
                <button
                  type="button"
                  disabled={laeuft !== null}
                  className="shrink-0 inline-flex items-center gap-1 font-label-md text-label-md text-primary hover:underline disabled:opacity-50"
                  onClick={() => void uebernehmen(v.schluessel)}
                >
                  <Icon name="download" className="text-[16px]" /> {laeuft === v.schluessel ? "Übernimmt…" : "Übernehmen"}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}
